import { Api } from "@core/api/api";
import { contentStateFromString, contentStateToString } from "@utils/richText/converters";
import { MediaDto, mediaFromDto } from "@domain/media/dtos";
import { ProfileData } from "@domain/user/profile";
import { CurrentProfile } from "./currentProfile";
import { ProfileDraft } from "./profileDraft";

export class CurrentProfileApi {
	constructor(private api: Api) {}

	async getCurrentProfile(): Promise<CurrentProfile> {
		const result = await this.api.get<CurrentProfileDto>("/profile");
		return currentProfileFromDto(result.data);
	}

	async register(profile: ProfileDraft): Promise<CurrentProfile> {
		const result = await this.api.post<CurrentProfileDto>("/profile", profileDraftToDto(profile));
		return currentProfileFromDto(result.data);
	}

	async update(profile: ProfileDraft): Promise<CurrentProfile> {
		const result = await this.api.put<CurrentProfileDto>("/profile", profileDraftToDto(profile));
		return currentProfileFromDto(result.data);
	}

	store(data: ProfileData): Promise<void> {
		return this.api.post("/profile/storage", { data: JSON.stringify(data) });
	}
	changeEmail(email: string): Promise<void> {
		return this.api.get(`/users/wish_change_email/${email}`);
	}
	async updateEmail(email: string): Promise<void> {
		return this.api.post("/users/change_email", { newEmail: email });
	}

	async delete(): Promise<void> {
		return this.api.get("/users/delete_account");
	}
}

function currentProfileFromDto(dto: CurrentProfileDto): CurrentProfile {
	const { description, presentation, birthDate, memberSince, customData, avatar, banner, avatarOriginal, avatarCrop } =
		dto;
	return {
		...dto,
		description: description ?? "",
		presentation: presentation ? contentStateFromString(presentation) : undefined,
		birthDate: birthDate ? new Date(birthDate) : undefined,
		memberSince: memberSince ? new Date(memberSince) : undefined,
		customData: customData ? JSON.parse(customData) : undefined,
		avatar: avatar ? mediaFromDto(avatar) : undefined,
		avatarOriginal: avatarOriginal ? mediaFromDto(avatarOriginal) : undefined,
		avatarCrop: avatarCrop ? mediaFromDto(avatarCrop) : undefined,
		banner: banner ? mediaFromDto(banner) : undefined,
		type: "USER",
	};
}

function profileDraftToDto(draft: ProfileDraft): ProfileDraftDto {
	return {
		...draft,
		description: draft.description ?? undefined,
		avatarId: draft.avatar?.id,
		avatarOriginalId: draft.avatarOriginal?.id,
		avatarCropId: draft.avatarCrop?.id,
		bannerId: draft.banner?.id,
		filterId: draft.filter?.id,
		presentation:
			draft.presentation && draft.presentation.getPlainText() ? contentStateToString(draft.presentation) : undefined,
	};
}

interface CurrentProfileDto extends Omit<
	CurrentProfile,
	"description" | "birthDate" | "customData" | "avatar" | "avatarOriginal" | "banner" | "presentation" | "avatarCrop"
> {
	description?: string;
	presentation?: string;
	birthDate?: string;
	customData?: string;
	avatar?: MediaDto;
	avatarOriginal?: MediaDto;
	avatarCrop?: MediaDto;
	banner?: MediaDto;
}

interface ProfileDraftDto extends Omit<
	ProfileDraft,
	"description" | "avatar" | "avatarOriginal" | "banner" | "presentation" | "avatarCrop" | "filter"
> {
	description?: string;
	presentation?: string;
	avatarId?: string;
	avatarOriginalId?: string;
	avatarCropId?: string;
	bannerId?: string;
	filterId?: string;
}
